import {
  Menu,
  Chip,
  Stack,
  Button,
  Divider,
  MenuItem,
  Typography,
  ListSubheader
} from "@material-ui/core";
import { Add, Message } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { getDataFromListingFieldKey } from "utils/contacts/bulkMessage/helper";
import type { BulkMessageRecipient } from "utils/contacts/bulkMessage/new";
import { useState, Fragment, type Dispatch, type MouseEvent, type SetStateAction } from "react";
import {
  getMissingFieldContactsCount,
  CUSTOM_BULK_MESSAGE_CONTACT_TEMPLATE_KEYS,
  type FieldTemplate,
  type ActiveFieldTemplate,
  type BulkMessageTemplateType,
  type CustomBulkMessageContactTemplateKey,
  type CustomTemplateField
} from "utils/contacts/bulkMessage/new";
import type { AlgoliaHit } from "instantsearch.js";
import type { ProcessedListingData } from "../../../../../@types/sharedSchema";

type BulkMessageDialogTemplateManagerProps = {
  /**
   * The recipients, as the dialog resolved them. Passed in rather than read from
   * `useLegacyContactsBook().allContacts`: that array is the whole book and is EMPTY on every host that
   * has not downloaded it, where the counts below were computed against nobody.
   */
  audience: BulkMessageRecipient[];
  listing: AlgoliaHit<ProcessedListingData> | null;
  selected: string[];
  bulkMessageType: BulkMessageTemplateType;
  activeTemplates: ActiveFieldTemplate;
  setActiveTemplates: Dispatch<SetStateAction<ActiveFieldTemplate>>;
  handleInsertTemplate: (template: string) => () => void;
  handleGetRandomMessage: () => void;
  customTemplateFields?: CustomTemplateField[];
  hideDefaultTemplates?: boolean;
};

export function BulkMessageDialogTemplateManager({
  listing,
  selected,
  audience,
  activeTemplates,
  bulkMessageType,
  setActiveTemplates,
  handleInsertTemplate,
  handleGetRandomMessage,
  customTemplateFields,
  hideDefaultTemplates
}: BulkMessageDialogTemplateManagerProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { t: translate } = useTranslation();

  const handleAddClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => setAnchorEl(null);

  const handleActiveTemplatesChange =
    (id: keyof ActiveFieldTemplate, active: boolean) => (): void => {
      const newActiveFilters = {
        ...activeTemplates,
        [id]: {
          ...activeTemplates[id],
          ...(!active && { value: null }),
          active: active
        }
      };

      setActiveTemplates(newActiveFilters);
    };

  const missingFieldCounts = getMissingFieldContactsCount(selected, audience);

  const allParsedTemplates = shapeDataForSelect(activeTemplates);
  const parsedActiveTemplates = hideDefaultTemplates
    ? allParsedTemplates.filter(({ type }) => type === "custom")
    : allParsedTemplates;

  const currentlyActiveTemplates = Object.values(activeTemplates).filter(({ active }) => active);

  return (
    <>
      <Typography variant="body1">
        {translate("contactsPage.bulkMessage.sendMessageToContacts", {
          contactsCount: selected.length
        })}
      </Typography>
      <Stack gap={2}>
        <Stack direction="row" gap={2} alignItems="center">
          <Button startIcon={<Message />} variant="outlined" onClick={handleGetRandomMessage}>
            {listing
              ? translate("contactsPage.control.createRandomMessageForListing")
              : translate("contactsPage.control.createRandomMessage")}
          </Button>
          <Button startIcon={<Add />} size="small" variant="outlined" onClick={handleAddClick}>
            {translate("contactsPage.bulkMessage.addTemplate")}
          </Button>
          <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}>
            {parsedActiveTemplates.map(({ type, options }) => (
              <Fragment key={type}>
                <ListSubheader sx={{ color: "black" }}>
                  {translate(`contactsPage.bulkMessage.templates.${type}.title`)}
                </ListSubheader>
                <Divider />
                {options.map(({ id, type, active }) => {
                  let isActive: boolean;

                  const isCustomContactKey = CUSTOM_BULK_MESSAGE_CONTACT_TEMPLATE_KEYS.includes(
                    id as CustomBulkMessageContactTemplateKey
                  );

                  if (type === "custom") {
                    isActive = true;
                  } else if (isCustomContactKey) {
                    isActive = true;
                  } else if (type === "contact") {
                    const hasAtLeastOneContactWithField = missingFieldCounts[id] < selected.length;

                    isActive = hasAtLeastOneContactWithField;
                  } else if (bulkMessageType === "listing" && type === "listing") {
                    isActive = Boolean(getDataFromListingFieldKey(id, listing));
                  } else if (bulkMessageType === "contact" && type === "listing") {
                    isActive = false;
                  }

                  if (active) isActive = false;

                  // Custom fields use their label directly; built-in fields use i18n
                  const customField = customTemplateFields?.find((f) => f.id === id);
                  const label = customField
                    ? customField.label
                    : translate(`contactsPage.bulkMessage.templates.${type}.fields.${id}`);

                  return (
                    <MenuItem
                      disabled={!isActive}
                      onClick={handleActiveTemplatesChange(id, !active)}
                      key={id}
                    >
                      {label}
                    </MenuItem>
                  );
                })}
              </Fragment>
            ))}
          </Menu>
        </Stack>
        <Stack direction="row" gap={2} flexWrap="wrap" alignItems="center">
          {currentlyActiveTemplates.map(({ id, type }) => {
            const customField = customTemplateFields?.find((f) => f.id === id);
            const label = customField
              ? customField.label
              : translate(`contactsPage.bulkMessage.templates.${type}.fields.${id}`);

            return (
              <Chip
                variant="outlined"
                label={label}
                onClick={handleInsertTemplate(id)}
                onDelete={handleActiveTemplatesChange(id, false)}
                key={id}
              />
            );
          })}
        </Stack>
      </Stack>
    </>
  );
}

export type SelectGroup = {
  type: BulkMessageTemplateType;
  options: FieldTemplate[];
};

export function shapeDataForSelect(ActiveTemplate: ActiveFieldTemplate): SelectGroup[] {
  const groupedOptions: Record<string, FieldTemplate[]> = {
    contact: [],
    listing: [],
    custom: []
  };

  for (const key in ActiveTemplate) {
    const template = ActiveTemplate[key];
    const group = groupedOptions[template.type] ?? (groupedOptions[template.type] = []);
    group.push(template);
  }

  return Object.entries(groupedOptions)
    .filter(([, options]) => options.length > 0)
    .map(([type, options]) => ({
      type: type as BulkMessageTemplateType,
      options
    }));
}
